<template>
  <div class="target-statechange-input">
    <button-item @click="onButtonClick">
      {{ target.title }}
    </button-item>
    <button class="button-icon" type="button" @click="$emit('remove')">
      <lucide-icon name="x" />
    </button>
    <div class="target-statechange-input__controls">
      <button-item class="button-item_toggle" @click="switchValueOperator">
        {{ valueOperatorLabel }}
      </button-item>
      <input-number
        :value="absChange"
        :max="100"
        step="any"
        @update:value="setValue"
      />
      <input-switch
        class="target-statechange-input__activeswitch"
        :value="change.active"
        @update:value="setActive"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useTargetStore } from "@editor/items/target/targets.store";
import { useInspectorView } from "@editor/views/inspector/inspectorView.store";

import { useOverlay } from "@editor/ui/overlayDialog.store";

import ButtonItem from "@core/input/ButtonItem.vue";
import InputNumber from "@core/input/InputNumber.vue";
import InputSwitch from "@core/input/InputSwitch.vue";
import LucideIcon from "@core/ui/LucideIcon.vue";

import type { Handleable } from "@core/types/Events";
import type { StateChange } from "@editor/views/inspector/inspectorView.store";

const props = defineProps<{
  change: { id: number; value: number; active: boolean };
}>();

const emit = defineEmits(["remove"]);

const valueOperatorLabel = computed(() => {
  if (props.change.value < 0) return "-";
  else return "+";
});

const targetId = computed(() => {
  return props.change.id;
});

const target = computed(() => {
  const targetStore = useTargetStore();
  return targetStore.items[targetId.value];
});

const absChange = computed(() => {
  return Math.abs(props.change.value);
});

function onButtonClick(mouseEvent: MouseEvent & Handleable) {
  if (mouseEvent.hasBeenHandled === true) return;
  mouseEvent.hasBeenHandled = true;
  showInfoOverlay();
}

function setValue(value: number) {
  const sign = Math.sign(props.change.value) || 1;
  update("value", value * sign);
}

function setActive(value: boolean) {
  update("active", value);
}

function switchValueOperator() {
  update("value", -props.change.value);
}

function update<T extends keyof StateChange>(key: T, value: StateChange[T]) {
  const newChange = {
    id: props.change.id,
    value: props.change.value,
    active: props.change.active
  };
  newChange[key] = value as (typeof newChange)[T];
  const inspectorView = useInspectorView();
  inspectorView.setTargetStateChange(newChange);
}

function showInfoOverlay() {
  useOverlay().setItem(target.value);
}
</script>

<style lang="scss">
.target-statechange-input {
  background-color: var(--color-background-deep);
  display: grid;
  grid-template-columns: 1fr 3rem;

  &__controls {
    display: grid;
    grid-template-columns: 2.5rem 3.5rem 1fr 6rem;
    gap: 0.5rem;
    padding: 0.5rem 0;
    align-items: center;
  }
  &__activeswitch {
    grid-column-start: 4;
  }
}
</style>
